import {
  afterEveryRender,
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { v7 as uuidv7 } from 'uuid';
import { DatePipe } from '@angular/common';
import {
  ConversationMessage,
  MessageOrigin,
  SendMessageRequest,
} from '../../shared/models/job-data.model';
import { VendorOrder } from '../../shared/models/vendor-order.model';
import { S3FilesService } from '../../shared/services/s3files.service';
import { S3DestinationFolder } from '../../shared/models/s3-destination-folder';
import { ToastService } from '../../shared/services/toast.service';
import { VendorOrdersService } from '../../core/layout/pages/translation-manager/services/vendor-orders.service';
import { AuthService } from '../../core/auth/services/auth.service';
import { MatIcon } from '@angular/material/icon';
import {
  MESSAGE_CHAR_LIMIT,
  ATTACHMENT_MAX_SIZE_BYTES,
  ATTACHMENT_MAX_FILES,
  ATTACHMENT_MAX_FILENAME_LENGTH,
  ATTACHMENT_ALLOWED_ACCEPT,
  ATTACHMENT_ALLOWED_EXTENSIONS,
  ATTACHMENT_FORBIDDEN_CHARS,
} from './messages.constants';
import { JOB_STATUSES_ACTIVE } from '../../shared/config/constants';
import { formatDisplayName } from '../../shared/utils/name-format.utils';

export interface StagedAttachment {
  file: File;
  name: string;
}

interface MessageAttachment {
  fileName: string;
  uploadId?: string;
}

interface MessageGroup {
  date: string;
  messages: IndexedMessage[];
}

interface IndexedMessage extends Omit<ConversationMessage, 'attachments'> {
  originalIndex: number;
  attachments: MessageAttachment[];
}

@Component({
  selector: 'app-messages',
  imports: [DatePipe, MatIcon],
  templateUrl: './messages.component.html',
  styleUrl: './messages.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MessagesComponent {
  detail = input.required<VendorOrder>();
  readOnly = input(false);
  messageSent = output<void>();
  newMessage = signal('');
  stagedAttachments = signal<StagedAttachment[]>([]);
  sending = signal(false);
  private readonly localMessages = signal<ConversationMessage[]>([]);
  private readonly expandState = signal<Record<number, number>>({});
  private readonly overflowingMessages = signal<Set<number>>(new Set());
  private static readonly DEFAULT_VISIBLE_LINES = 8;
  private static readonly MESSAGES_PER_PAGE = 10;
  private readonly hostEl = inject(ElementRef);

  readonly loadedPages = signal(1);
  readonly chatArea = viewChild<ElementRef<HTMLElement>>('chatArea');
  private hasScrolledToBottom = false;
  private pendingScrollRestore: { prevScrollHeight: number; prevScrollTop: number } | null = null;
  private pendingScrollToExpanded: number | null = null;
  private pendingCollapseRestore: { originalIndex: number; viewportOffset: number } | null = null;
  private needsOverflowCheck = true;

  constructor() {
    effect(() => {
      this.groupedMessages();
      this.expandState();
      this.needsOverflowCheck = true;
    });

    afterEveryRender(() => {
      if (this.needsOverflowCheck) {
        this.needsOverflowCheck = false;
        this.checkOverflows();
      }
      this.handlePendingScrolls();
    });
    afterNextRender(() => {
      const detail = this.detail();
      const messagesId = detail.messagesId;
      const jobUid = detail.jobUid;
      if (!messagesId || !jobUid) return;
      const admin = (detail.messages ?? []).some((m) => m.origin === MessageOrigin.admin);
      this.vendorOrdersService.markMessagesAsRead(messagesId, admin, jobUid).catch(() => {});
    });
  }

  readonly charLimit = MESSAGE_CHAR_LIMIT;
  readonly allowedAccept = ATTACHMENT_ALLOWED_ACCEPT;

  private readonly s3FilesService = inject(S3FilesService);
  private readonly toastService = inject(ToastService);
  private readonly vendorOrdersService = inject(VendorOrdersService);
  private readonly authService = inject(AuthService);
  readonly fileInput = viewChild<ElementRef<HTMLInputElement>>('fileInput');
  readonly messageInput = viewChild<ElementRef<HTMLTextAreaElement>>('messageInput');

  readonly vendorName = computed(() => {
    if (this.detail().vendorName) {
      return this.detail().vendorName!;
    }
    if (this.detail().vendor?.name) {
      return this.detail().vendor!.name;
    }
    const estimates = this.detail().estimates ?? [];
    const active = estimates.find(
      (e) => !!e.jobStatus && (JOB_STATUSES_ACTIVE as readonly string[]).includes(e.jobStatus),
    );
    return active?.name ?? '';
  });

  readonly contactName = computed(() => {
    const email = this.detail().email ?? '';
    const localPart = email.split('@')[0] ?? '';
    const parts = localPart.split('.');
    if (parts.length < 2) return localPart;
    const firstName = parts[0];
    const lastName = parts.at(-1)!;
    const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
    return `${capitalize(lastName)}, ${capitalize(firstName)}`;
  });

  readonly contactOrgLevel = computed(() => {
    const d = this.detail();
    return d.orgLevel6 || d.orgLevel5 || d.orgLevel4 || d.orgLevel3 || '';
  });

  readonly headerTitle = computed(() => {
    const vendor = this.vendorName();
    const base = vendor || this.contactName();
    if (this.adminParticipantName()) {
      return `AZ Translate Admin and ${base}`;
    }
    return base;
  });

  readonly adminParticipantName = computed(() => {
    const all = this.allSortedMessages();
    const firstAdmin = all.find((m) => m.origin === MessageOrigin.admin);
    if (!firstAdmin) return '';
    return this.formatAdminName(firstAdmin.author);
  });

  readonly headerSubtitle = computed(() => {
    return this.contactOrgLevel();
  });

  readonly charCount = computed(() => this.newMessage().length);

  readonly isCharLimitReached = computed(
    () => this.charCount() >= this.charLimit,
  );

  readonly canSend = computed(() => {
    return (
      (this.newMessage().trim().length > 0 ||
        this.stagedAttachments().length > 0) &&
      !this.readOnly()
    );
  });

  private readonly allSortedMessages = computed(() => {
    const detailMessages = this.detail().messages ?? [];
    const allMessages = [...detailMessages, ...this.localMessages()];
    const indexed: IndexedMessage[] = allMessages.map((msg, i) => ({
      ...msg,
      originalIndex: i,
      attachments: (msg.attachments ?? []).map((a: string | { fileName: string; uploadId?: string }): MessageAttachment =>
        typeof a === 'string' ? { fileName: a } : { fileName: a.fileName, uploadId: a.uploadId },
      ),
    }));
    return [...indexed].sort(
      (a, b) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
    );
  });

  readonly hasOlderMessages = computed(() => {
    const total = this.allSortedMessages().length;
    const visible = this.loadedPages() * MessagesComponent.MESSAGES_PER_PAGE;
    return total > visible;
  });

  readonly groupedMessages = computed(() => {
    const all = this.allSortedMessages();
    const visibleCount = this.loadedPages() * MessagesComponent.MESSAGES_PER_PAGE;
    const visible = all.slice(Math.max(0, all.length - visibleCount));
    const groups: MessageGroup[] = [];
    let currentDateStr = '';

    for (const msg of visible) {
      const dateStr = this.formatDateLabel(new Date(msg.timestamp));
      if (dateStr !== currentDateStr) {
        currentDateStr = dateStr;
        groups.push({ date: dateStr, messages: [] });
      }
      groups.at(-1)?.messages.push(msg);
    }

    return groups;
  });

  isVendorMessage(msg: ConversationMessage): boolean {
    return msg.origin === MessageOrigin.vendor;
  }

  isAdminMessage(msg: ConversationMessage): boolean {
    return msg.origin === MessageOrigin.admin;
  }

  isOwnMessage(msg: ConversationMessage): boolean {
    return msg.origin === MessageOrigin.user;
  }

  readonly firstAdminOriginalIndex = computed(() => {
    const groups = this.groupedMessages();
    for (const group of groups) {
      for (const msg of group.messages) {
        if (msg.origin === MessageOrigin.admin) {
          return msg.originalIndex;
        }
      }
    }
    return -1;
  });

  shouldShowSender(messages: IndexedMessage[], index: number): boolean {
    if (messages[index].origin === MessageOrigin.user) return false;
    if (index === 0) return true;
    return messages[index].author !== messages[index - 1].author;
  }

  hasNextConsecutive(messages: IndexedMessage[], index: number): boolean {
    const current = messages[index];
    const next = messages[index + 1];
    if (!next) return false;
    if (current.author !== next.author) return false;
    const diff = Math.abs(
      new Date(next.timestamp).getTime() - new Date(current.timestamp).getTime(),
    );
    return diff <= 60_000;
  }

  isConsecutiveWithPrevious(messages: IndexedMessage[], index: number): boolean {
    if (index === 0) return false;
    const current = messages[index];
    const prev = messages[index - 1];
    if (current.author !== prev.author) return false;
    const diff = Math.abs(
      new Date(current.timestamp).getTime() - new Date(prev.timestamp).getTime(),
    );
    return diff <= 60_000;
  }

  getMaxLines(originalIndex: number): number {
    return this.expandState()[originalIndex] ?? MessagesComponent.DEFAULT_VISIBLE_LINES;
  }

  isTextClamped(msg: IndexedMessage): boolean {
    if (!msg.message) return false;
    const maxLines = this.getMaxLines(msg.originalIndex);
    return maxLines > 0;
  }

  needsReadMore(msg: IndexedMessage): boolean {
    return this.overflowingMessages().has(msg.originalIndex);
  }

  isExpanded(originalIndex: number): boolean {
    return (
      this.getMaxLines(originalIndex) > MessagesComponent.DEFAULT_VISIBLE_LINES &&
      !this.overflowingMessages().has(originalIndex)
    );
  }

  showMore(originalIndex: number): void {
    this.expandState.update((state) => ({
      ...state,
      [originalIndex]: this.getMaxLines(originalIndex) + MessagesComponent.DEFAULT_VISIBLE_LINES,
    }));
    this.pendingScrollToExpanded = originalIndex;
  }

  collapse(originalIndex: number): void {
    const chatEl = this.chatArea()?.nativeElement;
    const msgEl = (this.hostEl.nativeElement as HTMLElement).querySelector<HTMLElement>(
      `[data-msg-index="${originalIndex}"]`,
    );
    const bubble = msgEl?.closest('.messages__bubble-row') as HTMLElement | null;
    if (chatEl && bubble) {
      this.pendingCollapseRestore = {
        originalIndex,
        viewportOffset: bubble.getBoundingClientRect().top - chatEl.getBoundingClientRect().top,
      };
    }
    this.expandState.update((state) => {
      const { [originalIndex]: _, ...rest } = state;
      return rest;
    });
  }

  private checkOverflows(): void {
    const el = this.hostEl.nativeElement as HTMLElement;
    const textEls = el.querySelectorAll<HTMLElement>('.messages__text--clamped');
    const overflowing = new Set<number>();
    textEls.forEach((textEl) => {
      if (textEl.scrollHeight > textEl.clientHeight) {
        const idx = Number(textEl.dataset['msgIndex']);
        if (!Number.isNaN(idx)) {
          overflowing.add(idx);
        }
      }
    });
    const current = this.overflowingMessages();
    if (overflowing.size !== current.size || [...overflowing].some((v) => !current.has(v))) {
      this.overflowingMessages.set(overflowing);
    }
  }

  private handlePendingScrolls(): void {
    if (this.pendingScrollToExpanded !== null) {
      const idx = this.pendingScrollToExpanded;
      this.pendingScrollToExpanded = null;
      this.scrollToExpandedBubble(idx);
      this.hasScrolledToBottom = true;
      return;
    }

    if (this.pendingCollapseRestore) {
      const restore = this.pendingCollapseRestore;
      this.pendingCollapseRestore = null;
      this.restoreScrollAfterCollapse(restore);
      this.hasScrolledToBottom = true;
      return;
    }

    if (this.pendingScrollRestore) {
      this.restoreScrollAfterLoad();
      return;
    }

    if (!this.hasScrolledToBottom) {
      this.scrollToBottomOnInit();
    }
  }

  private scrollToBottomOnInit(): void {
    const el = this.chatArea()?.nativeElement;
    if (!el || el.scrollHeight <= 0) return;
    el.scrollTop = el.scrollHeight;
    this.hasScrolledToBottom = true;
  }

  private scrollToExpandedBubble(originalIndex: number): void {
    const chatEl = this.chatArea()?.nativeElement;
    if (!chatEl) return;
    const el = this.hostEl.nativeElement as HTMLElement;
    const msgEl = el.querySelector<HTMLElement>(`[data-msg-index="${originalIndex}"]`);
    if (!msgEl) return;
    const bubble = msgEl.closest('.messages__bubble-row') as HTMLElement | null;
    if (!bubble) return;
    const chatRect = chatEl.getBoundingClientRect();
    const bubbleRect = bubble.getBoundingClientRect();
    if (bubbleRect.bottom > chatRect.bottom) {
      chatEl.scrollTop += bubbleRect.bottom - chatRect.bottom;
    }
  }

  loadPreviousMessages(): void {
    const el = this.chatArea()?.nativeElement;
    const prevScrollHeight = el?.scrollHeight ?? 0;
    const prevScrollTop = el?.scrollTop ?? 0;

    this.loadedPages.update((p) => p + 1);

    this.pendingScrollRestore = { prevScrollHeight, prevScrollTop };
  }

  private restoreScrollAfterLoad(): void {
    const el = this.chatArea()?.nativeElement;
    if (!el || !this.pendingScrollRestore) return;
    const { prevScrollHeight, prevScrollTop } = this.pendingScrollRestore;
    const delta = el.scrollHeight - prevScrollHeight;
    el.scrollTop = prevScrollTop + delta;
    this.pendingScrollRestore = null;
  }

  private restoreScrollAfterCollapse(restore: { originalIndex: number; viewportOffset: number }): void {
    const chatEl = this.chatArea()?.nativeElement;
    if (!chatEl) return;
    const msgEl = (this.hostEl.nativeElement as HTMLElement).querySelector<HTMLElement>(
      `[data-msg-index="${restore.originalIndex}"]`,
    );
    const bubble = msgEl?.closest('.messages__bubble-row') as HTMLElement | null;
    if (!bubble) return;
    const chatRect = chatEl.getBoundingClientRect();
    const currentOffset = bubble.getBoundingClientRect().top - chatRect.top;
    chatEl.scrollTop += currentOffset - restore.viewportOffset;

    // If scroll was clamped and the bubble is off-screen, bring it into view
    const adjustedOffset = bubble.getBoundingClientRect().top - chatRect.top;
    if (adjustedOffset < 0 || adjustedOffset + bubble.offsetHeight > chatEl.clientHeight) {
      chatEl.scrollTop += adjustedOffset;
    }
  }

  formatFileSize(bytes: number): string {
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(3)} MB`;
  }

  getFileBaseName(filename: string): string {
    const { base } = this.splitFilename(filename);
    return base;
  }

  formatAuthor(author: string): string {
    const vendorPrefix = this.detail().vendor?.name ?? '';
    if (vendorPrefix && author.startsWith(vendorPrefix)) {
      return vendorPrefix;
    }
    return this.formatAdminName(author);
  }

  formatAdminAuthor(author: string): string {
    const formatted = this.formatAdminName(author);
    return `AZ Translate Administrator (${formatted})`;
  }

  formatAdminName(name: string): string {
    return formatDisplayName(name);
  }

  private getSenderDisplayName(): string {
    const payload = this.authService.currentUserValue?.payload;
    if (payload?.name) {
      return payload.name;
    }
    return this.detail().email ?? 'User';
  }

  async onSend(): Promise<void> {
    if (!this.canSend() || this.readOnly() || this.sending()) return;
    const text = this.newMessage().trim();
    const attachments = this.stagedAttachments();
    if (!text && attachments.length === 0) return;

    this.sending.set(true);

    try {
      const detail = this.detail();

      const uploadedAttachments: { fileName: string; uploadId: string }[] = [];
      for (const attachment of attachments) {
        const uploadId = uuidv7();
        const { url } = await this.s3FilesService.getPresignedUrlForFile(
          attachment.name,
          S3DestinationFolder.CONVERSATIONS,
          false,
          detail.id,
          undefined,
          uploadId,
        );
        await this.s3FilesService.uploadFile(url, attachment.file);
        uploadedAttachments.push({ fileName: attachment.name, uploadId });
      }

      const messageRequest: SendMessageRequest = {
        jobInfo: {
          id: detail.id ?? '',
          translatedDate: detail.translatedDate ?? new Date(),
          user: detail.email ?? '',
          jobName: detail.customJobName ?? '',
          jobNumber: detail.jobNumber ?? '',
          email: detail.email ?? '',
          vendorName: detail.vendorName ?? detail.vendor?.name ?? '',
          status: detail.jobStatus ?? '',
          jobUid: detail.jobUid ?? '',
          ...(detail.messagesId ? { messagesId: detail.messagesId } : {}),
        },
        message: text,
        attachments: uploadedAttachments,
      };

      await this.vendorOrdersService.sendMessage(messageRequest);

      this.newMessage.set('');
      this.stagedAttachments.set([]);
      this.resetFileInput();
      this.resetTextarea();

      this.localMessages.update((msgs) => [
        ...msgs,
        {
          author: this.getSenderDisplayName(),
          message: text,
          timestamp: new Date() as unknown as Date,
          origin: MessageOrigin.user,
          attachments: uploadedAttachments.map(({ fileName, uploadId }) => ({ fileName, uploadId })),
        },
      ]);
      this.hasScrolledToBottom = false;
      this.messageSent.emit();
    } catch {
      this.toastService.showAtElement(this.hostEl.nativeElement, 'Failed to send message. Please try again.');
    } finally {
      this.sending.set(false);
    }
  }

  onInputChange(event: Event): void {
    if (this.readOnly()) return;
    const target = event.target as HTMLTextAreaElement;
    const value = target.value;
    if (value.length <= this.charLimit) {
      this.newMessage.set(value);
    } else {
      this.newMessage.set(value.slice(0, this.charLimit));
      target.value = this.newMessage();
    }
    this.autoResizeTextarea(target);
  }

  onKeydown(event: KeyboardEvent): void {
    if (this.readOnly()) return;
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.onSend();
    }
  }

  onAttachClick(): void {
    if (this.readOnly()) return;
    this.fileInput()?.nativeElement.click();
  }

  onFilesSelected(event: Event): void {
    if (this.readOnly()) {
      this.resetFileInput();
      return;
    }
    const input = event.target as HTMLInputElement;
    const files = input.files;
    if (!files) return;

    const current = this.stagedAttachments();
    const remaining = ATTACHMENT_MAX_FILES - current.length;

    if (files.length > remaining) {
      this.toastService.showAtElement(this.hostEl.nativeElement, `Maximum ${ATTACHMENT_MAX_FILES} files allowed per message.`, { type: 'error' });
    }

    const filesToAdd = Array.from(files).slice(0, remaining);
    const valid: StagedAttachment[] = [];
    const existingNames = new Set(current.map((a) => a.name));

    for (const file of filesToAdd) {
      if (existingNames.has(file.name)) {
        this.toastService.showAtElement(this.hostEl.nativeElement, `A file named "${file.name}" is already attached to this message.`, { type: 'error' });
        continue;
      }
      const error = this.validateFile(file);
      if (error) {
        this.toastService.showAtElement(this.hostEl.nativeElement, error, { type: 'error' });
        continue;
      }
      valid.push({ file, name: file.name });
      existingNames.add(file.name);
    }

    if (valid.length > 0) {
      this.stagedAttachments.update((prev) => [...prev, ...valid]);
    }

    this.resetFileInput();
  }

  removeAttachment(index: number): void {
    if (this.readOnly()) return;
    this.stagedAttachments.update((prev) => prev.filter((_, i) => i !== index));
  }

  async downloadAttachment(attachment: MessageAttachment, messageIndex: number): Promise<void> {
    try {
      const { url } = await this.s3FilesService.getPresignedUrlForFile(
        attachment.fileName,
        S3DestinationFolder.CONVERSATIONS,
        true,
        this.detail().id,
        undefined,
        attachment.uploadId ?? messageIndex,
      );
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = blobUrl;
      anchor.download = attachment.fileName;
      anchor.click();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10_000);
    } catch {
      this.toastService.showAtElement(this.hostEl.nativeElement, 'Failed to download file. Please try again.', { type: 'error' });
    }
  }

  private validateFile(file: File): string | null {
    const dotIndex = file.name.lastIndexOf('.');
    const ext = dotIndex >= 0 ? file.name.slice(dotIndex) : '';
    if (
      !ATTACHMENT_ALLOWED_EXTENSIONS.includes(
        ext.toLowerCase() as (typeof ATTACHMENT_ALLOWED_EXTENSIONS)[number],
      )
    ) {
      return `"${file.name}" has an unsupported file type. Allowed: ${ATTACHMENT_ALLOWED_EXTENSIONS.join(', ')}`;
    }

    if (file.size > ATTACHMENT_MAX_SIZE_BYTES) {
      return `"${file.name}" exceeds the 3MB size limit.`;
    }

    if (file.name.length > ATTACHMENT_MAX_FILENAME_LENGTH) {
      return `"${file.name}" exceeds the ${ATTACHMENT_MAX_FILENAME_LENGTH}-character filename limit.`;
    }

    if (ATTACHMENT_FORBIDDEN_CHARS.test(file.name.replace(/\.[^.]+$/, ''))) {
      return `"${file.name}" contains forbidden characters in the filename.`;
    }

    return null;
  }

  getFileExtension(filename: string): string {
    const { suffix } = this.splitFilename(filename);
    return suffix;
  }

  private splitFilename(filename: string): { base: string; suffix: string } {
    const match = /^(.+?)(\s*[(_]?v?\d+\)?\.[\w.]+)$/i.exec(filename);
    if (match) {
      return { base: match[1], suffix: match[2] };
    }
    const dotIndex = filename.lastIndexOf('.');
    if (dotIndex >= 0) {
      return { base: filename.slice(0, dotIndex), suffix: filename.slice(dotIndex) };
    }
    return { base: filename, suffix: '' };
  }

  private resetFileInput(): void {
    const el = this.fileInput()?.nativeElement;
    if (el) el.value = '';
  }

  private autoResizeTextarea(el: HTMLTextAreaElement): void {
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }

  private resetTextarea(): void {
    const el = this.messageInput()?.nativeElement;
    if (el) {
      el.value = '';
      el.style.height = 'auto';
    }
  }

  private formatDateLabel(date: Date): string {
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    if (this.isSameDay(date, today)) return 'Today';
    if (this.isSameDay(date, yesterday)) return 'Yesterday';

    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleDateString('en-GB', { month: 'short' });
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  private isSameDay(a: Date, b: Date): boolean {
    return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  }
}
